import { useState, useEffect } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import { supabase } from '../lib/supabase';

interface OrderTrackingSettings {
  enabled: boolean;
  shopify_enabled: boolean;
  prestashop_enabled: boolean;
  quick_action_text: string;
  response_template: string;
  prompt_message: string;
  error_message?: string;
  webhook_enabled?: boolean;
  zapier_webhook_url?: string;
  make_webhook_url?: string;
  zapier_token?: string;
  make_token?: string;
  buttonVisible?: boolean;
}

interface OrderDetails {
  order_number: string;
  order_status: string;
  additional_info: string;
  created_at: string;
  updated_at: string;
  total: string;
  currency: string;
  items?: {
    name: string;
    quantity: number;
    price: number;
  }[];
  shipping_address?: {
    name: string;
    address1: string;
    city: string;
    zip: string;
    country: string;
  };
  tracking_number?: string;
  tracking_url?: string;
  estimated_delivery?: string;
}

export function useOrderTracking() {
  const { chatbotPublicId } = useChatbot();
  const [settings, setSettings] = useState<OrderTrackingSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize settings
  useEffect(() => {
    const initializeSettings = async () => {
      if (!chatbotPublicId) return;
      
      setLoading(true);
      try {
        // Ensure the order_tracking_logs table exists
        try {
          await supabase.rpc("create_order_tracking_logs_if_not_exists");
        } catch (err) {
          // Continue even if this fails - the table might already exist
        }
        
        // Get order tracking settings
        const { data: configData, error: configError } = await supabase
          .from('chatbot_configs')
          .select('settings')
          .eq('chatbot_public_id', chatbotPublicId)
          .single();
        
        if (configError && configError.code !== 'PGRST116') throw configError;
        
        // Déterminer où se trouvent les paramètres de suivi de commande
        let orderTrackingSettings = null;
        
        if (configData?.settings) {
          if (configData.settings.order_tracking) {
            // Nouvelle structure: paramètres dans un objet order_tracking
            orderTrackingSettings = configData.settings.order_tracking;
          } else if (configData.settings.enabled !== undefined && 
                     (configData.settings.shopify_enabled !== undefined || 
                      configData.settings.prestashop_enabled !== undefined || 
                      configData.settings.webhook_enabled !== undefined)) {
            // Ancienne structure: paramètres à la racine
            orderTrackingSettings = configData.settings;
          }
        }
        
        // Si des paramètres sont trouvés, les utiliser, sinon utiliser des valeurs par défaut
        if (orderTrackingSettings) {
          // Extract the value and explicitly convert to boolean
          const buttonVisibleValue = orderTrackingSettings.buttonVisible;
          const strictBooleanValue = buttonVisibleValue === true;
          
          const settings = {
            enabled: orderTrackingSettings.enabled || false,
            shopify_enabled: orderTrackingSettings.shopify_enabled || false,
            prestashop_enabled: orderTrackingSettings.prestashop_enabled || false,
            quick_action_text: orderTrackingSettings.quick_action_text || "Track my order",
            response_template: orderTrackingSettings.response_template || "Your order #{{order_number}} is currently {{order_status}}. {{additional_info}}",
            prompt_message: orderTrackingSettings.prompt_message || "Please enter your order number to track your order:",
            error_message: orderTrackingSettings.error_message || "Sorry, we couldn't find that order. Please check the order number and try again.",
            webhook_enabled: orderTrackingSettings.webhook_enabled || false,
            zapier_webhook_url: orderTrackingSettings.zapier_webhook_url || "",
            make_webhook_url: orderTrackingSettings.make_webhook_url || "",
            zapier_token: orderTrackingSettings.zapier_token || "",
            make_token: orderTrackingSettings.make_token || "",
            buttonVisible: true  // Forcer à true pour résoudre le problème de visibilité
          };
          
          setSettings(settings);
        } else {
          const defaultSettings = {
            enabled: false,
            shopify_enabled: false,
            prestashop_enabled: false,
            quick_action_text: "Track my order",
            response_template: "Your order #{{order_number}} is currently {{order_status}}. {{additional_info}}",
            prompt_message: "Please enter your order number to track your order:",
            error_message: "Sorry, we couldn't find that order. Please check the order number and try again.",
            webhook_enabled: false,
            zapier_webhook_url: "",
            make_webhook_url: "",
            zapier_token: "",
            make_token: "",
            buttonVisible: true
          };
          
          setSettings(defaultSettings);
        }
        
        setInitialized(true);
      } catch (err) {
        setError('Failed to load order tracking settings');
      } finally {
        setLoading(false);
      }
    };
    
    initializeSettings();
  }, [chatbotPublicId]);

  // Track an order
  const trackOrder = async (orderNumber: string): Promise<OrderDetails | null> => {
    if (!chatbotPublicId || !settings?.enabled) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      // Vérifier si au moins une intégration est activée
      const hasAnyIntegration = settings.webhook_enabled || settings.shopify_enabled || settings.prestashop_enabled;
      
      if (!hasAnyIntegration) {
        throw new Error('Aucune intégration n\'est activée. Veuillez activer au moins une intégration (Shopify, PrestaShop ou Webhook) dans les paramètres.');
      }
      
      // First check if we should use webhooks
      if (settings.webhook_enabled) {
        // Try Zapier first if configured
        if (settings.zapier_webhook_url && settings.zapier_token) {
          try {
            const response = await fetch(settings.zapier_webhook_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Zapier-Token': settings.zapier_token
              },
              body: JSON.stringify({
                order_number: orderNumber,
                chatbot_id: chatbotPublicId
              })
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Erreur de communication avec Zapier (${response.status}): ${errorText || 'Pas de détails disponibles'}`);
            }
            
            const data = await response.json();
            
            // Vérifier que la réponse contient les champs nécessaires
            if (!data.order_number || !data.order_status) {
              throw new Error('La réponse de Zapier ne contient pas les informations requises (order_number, order_status)');
            }
            
            // Log the webhook response
            try {
              await supabase
                .from("order_tracking_logs")
                .insert({
                  chatbot_public_id: chatbotPublicId,
                  order_number: orderNumber,
                  provider: 'zapier',
                  status: 'success',
                  response: data
                });
            } catch (logError) {
              // Continue even if logging fails
            }
            
            return data;
          } catch (zapierError) {
            // Continue to try Make if Zapier fails
          }
        } else if (settings.webhook_enabled) {
          // Webhook integration is enabled but Zapier URL or token is missing
        }
        
        // Try Make if configured
        if (settings.make_webhook_url && settings.make_token) {
          try {
            const response = await fetch(settings.make_webhook_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Make-Token': settings.make_token
              },
              body: JSON.stringify({
                order_number: orderNumber,
                chatbot_id: chatbotPublicId
              })
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Erreur de communication avec Make/Integromat (${response.status}): ${errorText || 'Pas de détails disponibles'}`);
            }
            
            const data = await response.json();
            
            // Vérifier que la réponse contient les champs nécessaires
            if (!data.order_number || !data.order_status) {
              throw new Error('La réponse de Make ne contient pas les informations requises (order_number, order_status)');
            }
            
            // Log the webhook response
            try {
              await supabase
                .from("order_tracking_logs")
                .insert({
                  chatbot_public_id: chatbotPublicId,
                  order_number: orderNumber,
                  provider: 'make',
                  status: 'success',
                  response: data
                });
            } catch (logError) {
              // Continue even if logging fails
            }
            
            return data;
          } catch (makeError) {
            if (settings.shopify_enabled || settings.prestashop_enabled) {
              // Falling back to built-in e-commerce integrations
            } else {
              throw new Error(`Erreur avec l'intégration Make: ${makeError instanceof Error ? makeError.message : 'Erreur inconnue'}`);
            }
          }
        } else if (settings.webhook_enabled && !settings.zapier_webhook_url && !settings.make_webhook_url) {
          throw new Error('L\'intégration Webhook est activée mais aucune URL de webhook (Zapier ou Make) n\'est configurée');
        }
      }
      
      // If webhooks are not enabled or failed, use built-in tracking
      // Determine which provider to use
      if (settings.shopify_enabled || settings.prestashop_enabled) {
        let provider = null;
        if (settings.shopify_enabled) {
          provider = 'shopify';
        } else if (settings.prestashop_enabled) {
          provider = 'prestashop';
        }
        
        if (!provider) {
          throw new Error('Les intégrations e-commerce sont activées mais aucune plateforme n\'est sélectionnée');
        }
        
        // Call the track-order edge function
        const { data, error: fnError } = await supabase.functions.invoke(
          'track-order',
          {
            body: {
              order_number: orderNumber,
              provider,
              chatbot_public_id: chatbotPublicId
            }
          }
        );
        
        if (fnError) {
          throw new Error(`Erreur lors du suivi de commande: ${fnError.message || 'Échec de communication avec le service de suivi'}`);
        }
        
        if (!data) {
          throw new Error(`Aucune information trouvée pour la commande ${orderNumber}`);
        }
        
        return data as OrderDetails;
      } else if (!settings.webhook_enabled) {
        // Si on arrive ici, c'est qu'aucune intégration n'est configurée correctement
        throw new Error('Aucune intégration n\'est correctement configurée. Veuillez activer et configurer au moins une intégration (Shopify, PrestaShop ou Webhook) dans les paramètres.');
      }
      
      // Si on arrive ici, c'est qu'aucune intégration n'a fonctionné
      throw new Error('Toutes les intégrations ont échoué. Veuillez vérifier vos paramètres de configuration.');
      
    } catch (err) {
      // Message d'erreur personnalisé ou message générique selon le cas
      const customErrorMessage = settings?.error_message || "Sorry, we couldn't find that order. Please check the order number and try again.";
      const technicalErrorMessage = err instanceof Error 
        ? err.message
        : 'Une erreur inconnue est survenue lors du suivi de votre commande. Veuillez réessayer plus tard.';
        
      // Pour les erreurs techniques spécifiques, on conserve le message d'erreur technique
      // Pour les erreurs de type "commande non trouvée", on utilise le message personnalisé
      const isOrderNotFoundError = technicalErrorMessage.includes("trouvée") || 
                                  technicalErrorMessage.includes("aucune information") || 
                                  technicalErrorMessage.includes("no data") ||
                                  !technicalErrorMessage.includes("erreur");
      
      const errorMessage = isOrderNotFoundError ? customErrorMessage : technicalErrorMessage;
      
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add a new function to update button visibility
  const updateButtonVisibility = async (visible: boolean) => {
    if (!chatbotPublicId || !settings) {
      return false;
    }
    
    setLoading(true);
    
    try {
      // Get the current settings
      const { data: configData, error: configError } = await supabase
        .from('chatbot_configs')
        .select('settings')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();
      
      if (configError) throw configError;
      
      if (!configData?.settings) {
        throw new Error('Settings not found');
      }
      
      // Create a safe copy of the settings
      let safeSettings = configData.settings;
      if (typeof safeSettings === 'string') {
        try {
          safeSettings = JSON.parse(safeSettings);
        } catch (err) {
          throw new Error('Invalid settings format');
        }
      }
      
      // Make sure order_tracking exists
      if (!safeSettings.order_tracking) {
        safeSettings.order_tracking = {
          ...settings,
          buttonVisible: visible
        };
      } else {
        // Update the buttonVisible property
        safeSettings.order_tracking = {
          ...safeSettings.order_tracking,
          buttonVisible: visible
        };
      }
      
      // Save to database
      const { error: updateError } = await supabase
        .from('chatbot_configs')
        .update({ settings: safeSettings })
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (updateError) throw updateError;
      
      // Update local state immediately
      setSettings({
        ...settings,
        buttonVisible: visible
      });
      
      return true;
    } catch (err) {
      setError('Failed to update button visibility');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Add a new function to set button visibility (not toggle)
  const setButtonVisibility = async (visible: boolean) => {
    if (!chatbotPublicId || !settings) return false;
    
    // Convert to strict boolean - force false when falsy
    const strictBoolean = visible === true;
    
    setLoading(true);
    
    try {
      // Get the current settings
      const { data: configData, error: configError } = await supabase
        .from('chatbot_configs')
        .select('settings')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();
      
      if (configError) {
        throw configError;
      }
      
      if (!configData?.settings) {
        throw new Error('Settings not found');
      }
      
      // Create a safe copy of the settings
      let safeSettings = configData.settings;
      if (typeof safeSettings === 'string') {
        try {
          safeSettings = JSON.parse(safeSettings);
        } catch (err) {
          throw new Error('Invalid settings format');
        }
      }
      
      // Make sure order_tracking exists
      if (!safeSettings.order_tracking) {
        safeSettings.order_tracking = {
          ...settings,
          buttonVisible: strictBoolean
        };
      } else {
        // Update the buttonVisible property
        safeSettings.order_tracking = {
          ...safeSettings.order_tracking,
          buttonVisible: strictBoolean
        };
      }
      
      // Save to database
      const { error: updateError } = await supabase
        .from('chatbot_configs')
        .update({ settings: safeSettings })
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (updateError) {
        throw updateError;
      }
      
      // Update local state immediately
      const updatedSettings = {
        ...settings,
        buttonVisible: strictBoolean
      };
      
      setSettings(updatedSettings);
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Format the response using the template
  const formatResponse = (orderDetails: OrderDetails): string => {
    if (!settings) return '';
    
    let response = settings.response_template;
    
    // Replace placeholders with actual values
    response = response.replace(/{{order_number}}/g, orderDetails.order_number);
    response = response.replace(/{{order_status}}/g, orderDetails.order_status);
    response = response.replace(/{{additional_info}}/g, orderDetails.additional_info);
    
    return response;
  };

  return {
    settings,
    loading,
    error,
    initialized,
    trackOrder,
    formatResponse,
    updateButtonVisibility,
    setButtonVisibility
  };
}